import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { BACKEND_URL } from "../lib/api";
import { logger } from "../lib/logger";
import { ArtworkCard } from "../components/ArtworkCard";

const resolveImg = (url) => (url && url.startsWith("/api") ? `${BACKEND_URL}${url}` : url);

export default function ArtistOfTheWeek() {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api
      .get("/artist-of-the-week")
      .then(({ data }) => setData(data))
      .catch((err) => logger.error("AOTW load failed:", err))
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded) {
    return <div className="p-24 label-eyebrow text-[#555]">Loading…</div>;
  }
  if (!data?.artist) {
    return (
      <div data-testid="aotw-empty" className="px-6 md:px-12 lg:px-24 py-32 max-w-2xl mx-auto text-center">
        <p className="label-eyebrow text-[#555]">— Artist of the Week</p>
        <h1 className="font-serif-display text-5xl tracking-tighter font-light mt-4">
          Coming soon
        </h1>
        <p className="text-[#555] mt-6">
          We&rsquo;re curating this week&rsquo;s spotlight. Check back in a few days.
        </p>
        <Link
          to="/artists"
          className="inline-block mt-8 label-eyebrow border border-[#111] px-8 py-4 hover:bg-[#111] hover:text-white transition-colors"
        >
          Browse all artists
        </Link>
      </div>
    );
  }

  const { artist, quote, artworks } = data;
  return (
    <div data-testid="aotw-page" className="px-6 md:px-12 lg:px-24 py-16">
      <p className="label-eyebrow text-[#1E3A8A]">— Artist of the Week</p>
      <div className="grid grid-cols-12 gap-y-10 gap-x-4 lg:gap-10 mt-6">
        <div className="col-span-12 lg:col-span-5">
          <div className="aspect-[4/5] bg-[#F2EDE5] overflow-hidden">
            <img
              src={resolveImg(artist.avatar_url) || "https://images.unsplash.com/photo-1500336624523-d727130c3328?w=800"}
              alt={`${artist.name}, AfricaArtMasters artist of the week${artist.country ? ` — ${artist.country}` : ""}`}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.src = "https://images.unsplash.com/photo-1500336624523-d727130c3328?w=800";
              }}
            />
          </div>
        </div>
        <div className="col-span-12 lg:col-span-7">
          <h1
            data-testid="aotw-artist-name"
            className="font-serif-display text-6xl md:text-7xl tracking-tighter leading-none font-light"
          >
            {artist.name}
          </h1>
          <p className="label-eyebrow text-[#1E3A8A] mt-6">{artist.country}</p>
          {quote && (
            <blockquote
              data-testid="aotw-quote"
              className="mt-10 border-l-4 border-[#F4C430] pl-6 italic font-serif-display text-2xl text-[#111] leading-snug max-w-xl"
            >
              &ldquo;{quote}&rdquo;
            </blockquote>
          )}
          {artist.bio && (
            <p className="mt-8 text-lg text-[#333] leading-relaxed font-light max-w-xl">
              {artist.bio}
            </p>
          )}
          <Link
            to={`/artist/${artist.id}`}
            data-testid="aotw-view-artist"
            className="inline-block mt-10 label-eyebrow border border-[#111] px-8 py-4 hover:bg-[#111] hover:text-white transition-colors"
          >
            View full profile →
          </Link>
        </div>
      </div>

      {artworks?.length > 0 && (
        <>
          <div className="hairline-divider my-20" />
          <p className="label-eyebrow text-[#555]">— Selected works</p>
          <h2 className="font-serif-display text-4xl tracking-tight font-light mt-2 mb-10">
            From the studio
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
            {artworks.map((art, i) => (
              <ArtworkCard key={art.id} artwork={art} aspect={i % 3 === 0 ? "tall" : "square"} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
